import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authenticate, PortalApiError } from '../lib/portalApi';

/**
 * PinGate — modal-style overlay rendered when the API returns 401
 * { error: { require_pin: true } }. The user types their PIN; on success
 * the Worker sets a session cookie and we invoke `onUnlocked` so the
 * parent re-runs its data queries.
 */
export function PinGate({
  slug,
  projectName,
  onUnlocked,
}: {
  slug: string;
  projectName?: string;
  onUnlocked: () => void;
}) {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);

  const mut = useMutation({
    mutationFn: () => authenticate(slug, pin),
    onSuccess: () => {
      onUnlocked();
    },
    onError: () => {
      setShake(true);
      setTimeout(() => setShake(false), 400);
    },
  });

  const errorMessage =
    mut.error instanceof PortalApiError
      ? mut.error.status === 401
        ? 'Incorrect PIN. Try again.'
        : mut.error.message
      : mut.error
        ? 'Could not connect. Check your network and try again.'
        : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
      <form
        className={shake ? 'animate-pulse' : undefined}
        onSubmit={(e) => {
          e.preventDefault();
          if (pin.trim()) mut.mutate();
        }}
      >
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-md p-8 max-w-md w-full">
          <h1 className="text-xl font-bold text-[var(--color-ink)] mb-1">Project portal</h1>
          {projectName && (
            <p className="text-sm text-[var(--color-ink-2)] mb-4">{projectName}</p>
          )}
          <p className="text-sm text-[var(--color-ink-2)] mb-4">
            Enter the PIN your project manager sent you.
          </p>
          <label className="block">
            <span className="label-mono">PIN</span>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-base num tracking-widest border border-[var(--color-border)] rounded bg-[var(--color-surface)] text-[var(--color-ink)] focus:outline-none focus:border-[var(--color-accent)]"
              placeholder="••••"
            />
          </label>
          {errorMessage && (
            <p className="mt-2 text-sm text-[var(--color-danger)]">{errorMessage}</p>
          )}
          <button
            type="submit"
            disabled={mut.isPending || !pin.trim()}
            className="mt-4 w-full px-4 py-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mut.isPending ? 'Checking…' : 'Continue'}
          </button>
        </div>
        <p className="mt-4 text-xs text-[var(--color-ink-3)] text-center">
          Rough-In Advisory LLC
        </p>
      </form>
    </div>
  );
}
